import clickSound from '../../assets/sounds/click.mp3';
import startSound from '../../assets/sounds/start.mp3';
import shutdownSound from '../../assets/sounds/shutdown.mp3';

export type SoundType = 'click' | 'start' | 'shutdown';

type Listener = () => void;

class SystemSoundsService {
  private sounds: Record<SoundType, HTMLAudioElement | null> = {
    click: null,
    start: null,
    shutdown: null,
  };

  private currentSound: HTMLAudioElement | null = null;

  private _loading = true;
  private _error: string | null = null;
  private _isMuted = false;
  private volume = 0.7;

  private listeners: Listener[] = [];

  constructor() {
    this.loadSounds();
  }

  // --------------------
  // Public Getters
  // --------------------
  get loading() {
    return this._loading;
  }

  get error() {
    return this._error;
  }

  get isMuted() {
    return this._isMuted;
  }

  // --------------------
  // Subscribe system
  // --------------------
  subscribe(listener: Listener) {
    this.listeners.push(listener);
  }

  unsubscribe(listener: Listener) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }

  // --------------------
  // Load Sounds
  // --------------------
  private loadSounds() {
    const soundFiles: Record<SoundType, string> = {
      click: clickSound,
      start: startSound,
      shutdown: shutdownSound,
    };

    const loadPromises = (Object.keys(soundFiles) as SoundType[]).map(
      (key) =>
        new Promise<void>((resolve, reject) => {
          const audio = new Audio();
          audio.src = soundFiles[key];
          audio.preload = 'auto';
          audio.volume = this.volume;

          const cleanup = () => {
            audio.removeEventListener('loadeddata', onLoad);
            audio.removeEventListener('error', onError);
          };

          const onLoad = () => {
            cleanup();
            resolve();
          };

          const onError = () => {
            cleanup();
            reject(`Failed to load ${key}`);
          };

          audio.addEventListener('loadeddata', onLoad);
          audio.addEventListener('error', onError);

          audio.load();

          this.sounds[key] = audio;
        }),
    );

    Promise.all(loadPromises)
      .then(() => {
        this._loading = false;
        this.notify();
      })
      .catch((err) => {
        console.error(err);
        this._error = String(err);
        this._loading = false;
        this.notify();
      });
  }

  // --------------------
  // Play
  // --------------------
  play(type: SoundType) {
    if (this._isMuted) return;

    const sound = this.sounds[type];
    if (!sound) return;

    if (this.currentSound) {
      this.currentSound.pause();
      this.currentSound.currentTime = 0;
    }

    sound.currentTime = 0;
    sound.play().catch(() => { });

    this.currentSound = sound;
  }

  // --------------------
  // Mute
  // --------------------
  mute(value: boolean) {
    this._isMuted = value;

    Object.values(this.sounds).forEach((audio) => {
      if (audio) {
        audio.muted = value;
      }
    });

    this.notify();
  }

  // Optional: Change volume dynamically
  setVolume(volume: number) {
    this.volume = volume;

    Object.values(this.sounds).forEach((audio) => {
      if (audio) {
        audio.volume = volume;
      }
    });
  }
}

export const systemSoundsService = new SystemSoundsService();